/**
 * Levr's legal and regulatory identity, in one place.
 *
 * WHY THIS FILE EXISTS, AND WHY MOST OF IT IS `null`:
 *
 * The disclaimer, privacy policy and terms all need to say who Levr actually is — the registered
 * entity, its NZBN, its Financial Service Provider number, and the dispute resolution scheme a
 * customer can escalate to. None of that is currently published anywhere on the site, which is a
 * trust gap and, for a financial service provider dealing with retail clients, very likely a
 * disclosure obligation as well.
 *
 * These are facts about a real company. They are NOT safe to guess: a wrong NZBN or an FSP number
 * that belongs to someone else is worse than none at all. So each field stays `null` until the
 * owner supplies the real value, and every consumer of this file renders only the fields that are
 * set. A blank field disappears; it never renders as "TBC" or an empty row in front of a customer.
 *
 * ✅ **DEFERRED ON PURPOSE — owner's decision, 2026-08-25.** Joel: "we'll add identity things
 * later." The disclaimer ships without them. This is an accepted disclosure gap, not an oversight
 * and not an unfinished task: do not re-raise it as a blocker, and do not invent values to close
 * it. Codex review flagged it three times before the call was made; the decision stands until Joel
 * says otherwise.
 *
 * TO COMPLETE, WHENEVER THAT IS: fill in the values below. No other file needs to change — the
 * disclaimer page and the footer both read from here.
 */

/**
 * The registered entity behind levr.nz.
 *
 * `legalName`   — e.g. 'Levr Limited', exactly as registered with the Companies Office.
 * `nzbn`        — 13-digit New Zealand Business Number.
 * `fspNumber`   — FSP registration, e.g. 'FSP123456'. A credit broker dealing with retail clients
 *                 generally must be on the Financial Service Providers Register AND belong to an
 *                 approved dispute resolution scheme. Confirm Levr's position with a compliance
 *                 adviser before publishing either field.
 * `disputeScheme` — the approved scheme Levr belongs to. In NZ that is one of: Financial Services
 *                 Complaints Limited (FSCL), the Insurance & Financial Services Ombudsman (IFSO),
 *                 or Financial Dispute Resolution Service (FDRS).
 * `disputeSchemeUrl`, `disputeSchemePhone` — so a customer can actually reach them.
 * `registeredAddress` — the registered office. `Auckland, New Zealand` is what the site says today;
 *                 a street address is stronger and is what a complaints process needs.
 */
export const LEGAL_ENTITY = {
  legalName: null,
  nzbn: null,
  fspNumber: null,
  disputeScheme: null,
  disputeSchemeUrl: null,
  disputeSchemePhone: null,
  registeredAddress: null,
};

/** What the site can say about itself today, with no unverified claims in it. */
export const TRADING_NAME = 'Levr';
export const SITE_DOMAIN = 'levr.nz';
export const CONTACT_EMAIL = 'hello@levr.nz';
export const CONTACT_LOCATION = 'Auckland, New Zealand';

/**
 * The public phone number. `null` hides it everywhere it was shown.
 *
 * Hidden on the owner's instruction (2026-08-25). The footer and the dashboard's "Need Help?" panel
 * both advertised `0800 LEVR NZ`, which is a vanity spelling rather than a provisioned line. Every
 * consumer renders the phone row only when this is set, so restoring it is one value here — and
 * when it is set, make it the dialable digits, because a customer cannot dial letters from a phone
 * that has none on the keypad.
 */
export const CONTACT_PHONE = null;

/**
 * Dealer referrals — already disclosed in the Terms of Service, so it is a known fact and the
 * disclaimer states it too rather than being quieter than the page next to it.
 */
export const DEALER_COMMISSION_DISCLOSURE =
  'If a vehicle dealer referred you to us, that dealer may be paid a commission by us if your '
  + 'application results in a funded loan. It does not change the rates or terms a lender offers you.';

/**
 * How Levr itself is paid.
 *
 * `null` until the owner confirms the actual arrangement. A broker that receives a commission from
 * the lender it places you with has an interest in which lender that is, and saying so plainly is
 * both the honest position and the one a regulator expects. Set this to a short sentence once
 * confirmed — e.g. 'Levr is paid a commission by the lender when a loan we arrange is settled.'
 *
 * Left empty rather than guessed: describing a remuneration arrangement inaccurately on a consumer
 * credit site is worse than not describing it yet.
 */
export const COMMISSION_DISCLOSURE = null;

/**
 * MoneyTalks — the free, independent, government-funded financial mentoring service.
 *
 * Pointing at it is standard practice on NZ consumer-credit sites and it is the right thing to do
 * regardless: someone who has landed on a car finance page and actually needs budgeting help should
 * be told where to get it for free. These details are public and stable.
 */
export const MONEYTALKS = {
  name: 'MoneyTalks',
  url: 'https://www.moneytalks.co.nz',
  phone: '0800 345 123',
};

/** True once there is at least one real registration detail worth showing. */
export function hasRegulatoryIdentity() {
  return Boolean(
    LEGAL_ENTITY.legalName
    || LEGAL_ENTITY.nzbn
    || LEGAL_ENTITY.fspNumber
    || LEGAL_ENTITY.disputeScheme,
  );
}
